/* eslint-disable react-hooks/exhaustive-deps */
import {
  Flex,
  Heading,
  Box,
  useToast,
  ToastId,
} from '@chakra-ui/react';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';

import {
  Cars,
  Races,
  Tier,
} from 'api/generated/graphql';
import { CarSelect } from 'components/RSelect/CarSelect';
import { RToast } from 'components';
import { FusionCarStats, StatValue } from 'components/Salvage/new/FusionCarStats';
import TournamentEntryAccordion from 'components/Accordions/TournamentEntryAccordion/TournamentEntryAccordion';
import LeaderboardAccordionPanel from 'components/LeaderboardAccordionPanel/LeaderboardAccordionPanel';
import { rarityLevels } from 'utils/themeConstants';
import TicketCheckoutModal from 'components/TicketCheckoutModal/TicketCheckoutModal';

export type LeaderboardAvailable = {
  available: boolean;
  car: Cars;
};

interface SelectCarProps {
  admissionFee?: number;
  cars: Cars[] | undefined;
  enterRaceIsSuccess?: boolean;
  raceStartTime?: string;
  race?: Races;
  isOpen: boolean;
  withLeaderboardAvailable?: LeaderboardAvailable[];
  withLeaderboardEntry: boolean;
  setWithLeaderboardEntry: Dispatch<SetStateAction<boolean | undefined>>;
  selectedCarId: string;
  setSelectedCarId: Dispatch<SetStateAction<string | undefined>>;
  onClose: () => void;
  idFromGarage?: string;
}

const SelectCar = ({
  admissionFee = 0,
  cars,
  raceStartTime,
  race,
  isOpen,
  withLeaderboardAvailable,
  withLeaderboardEntry,
  setWithLeaderboardEntry,
  selectedCarId,
  setSelectedCarId,
  onClose,
  idFromGarage,
}: SelectCarProps) => {
  // Toast
  const toast = useToast();
  const toastIdRef = useRef<ToastId | null>(null);

  // Car Selection
    
  const [selectedCarIndex, setSelectedCarIndex] = useState(0);
  const [showCarStats, setShowCarStats] = useState(true);

  const raceTier = race?.tier?.name?.toLowerCase();
  const selectedCar = cars?.[selectedCarIndex];
  const selectedCarAvailable = withLeaderboardAvailable?.find(
    (car) => car.car.id === selectedCar?.id
  )?.available;

  useEffect(() => {
    if (selectedCar) {
      setSelectedCarId(selectedCar.id);
    }
  }, [selectedCar, setSelectedCarId, selectedCarId]);


  const getNextDay3PMEST = () => {
    const now = new Date();
    const nextDay = new Date(now);
    nextDay.setUTCHours(19, 0, 0, 0);
    if (now > nextDay) {
      nextDay.setDate(now.getDate() + 1);
    }
    return nextDay.toISOString();
  };

  const setLeaderboardEntry = (
    value: boolean | ((prevState: boolean | undefined) => boolean | undefined)
  ) => {
    if (
      selectedCar?.tier?.rank === 0 ||
      selectedCar?.tier?.name?.toLowerCase() === 'common'
    ) {
      setWithLeaderboardEntry(false);
      toastIdRef.current = toast({
        position: 'bottom-right',
        render: () => (
          <RToast
            variant="error"
            title="Your car must be minted to enter the leaderboard"
            onClose={() => toast.close(toastIdRef.current as ToastId)}
          />
        ),
      });
      return;
    }

    setWithLeaderboardEntry((prev) =>
      typeof value === 'function' ? value(prev) : value
    );
  };

  const getCarStats = () => {
    if (!selectedCar?.stats) return null;

    const statObjects = selectedCar.stats.map(stat => ({
      bars: stat.bars,
      tuneUpValidUntil: stat.tuneUpValidUntil
    })) as StatValue[];

    const [speed, fastTurns, handling, slowTurns, cooling, braking, acceleration, stability] = statObjects || [];

    return { 
      speed, 
      fastTurns, 
      handling, 
      slowTurns, 
      cooling, 
      braking, 
      acceleration, 
      stability 
    };
  };

  useEffect(() => {
    getFirstAvailableCar();
  }, [cars]);


  const getLockedCarIds = () => {
    const difficulty = race?.difficulty || 0;
    const baseLockedIds: string[] = [];

    return (
      cars?.reduce((acc: string[], car) => {
        const rarity = car.rarity;

        // Add cars that are explicitly locked
        if (car.locked || race?.playersParticipants?.some(participant => participant.car.id === car.id)) {
          acc.push(car.id);
        }

        // Add cars based on difficulty rules
        if (
          (difficulty >= 2 && ['trial', 'common'].includes(rarity || '')) ||
          (difficulty >= 3 && rarity === 'uncommon') ||
          (difficulty >= 4 && rarity === 'rare') ||
          (difficulty >= 5 && rarity === 'epic')
        ) {
          acc.push(car.id);
        }

        return acc;
      }, baseLockedIds) || baseLockedIds
    );
  };

  const lockedCarIds = getLockedCarIds();
  const eligibleCars =
    cars?.filter((car) => {
      return !lockedCarIds.includes(car.id);
    }) || [];

  const getFirstAvailableCar = () => {
    if (idFromGarage) {
      const carFromGarageIndex = cars?.findIndex(car => car.id === idFromGarage)
      setSelectedCarIndex(carFromGarageIndex || 0);
    }
    else {
      const firstAvailableIndex =
        cars?.findIndex((car) => eligibleCars.includes(car)) || 0;
      setSelectedCarIndex(firstAvailableIndex);
    }
  };

  const { isOpen: isUpgradeOpen, onOpen: onUpgradeOpen, onClose: onUpgradeClose } = useDisclosure();
  const [selectedUpgradeCar, setSelectedUpgradeCar] = useState<Cars | null>(null);

  const handleUpgradeClick = (car: Cars) => {
    setSelectedUpgradeCar(car);
    onUpgradeOpen();
  };

  return (
    <>
      <Box height="fit-content" pb="10" className="grey-scrollbar">
        <Flex
          w="full"
          mt="0.5rem"
          mb="0.875rem"
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex flexDir="column">
            <Heading
              as="h1"
              fontSize={{ base: '18px', md: '24px' }}
              lineHeight={{ base: '24px', md: '28px' }}
              fontWeight={400}
              textTransform="uppercase"
              background="linear-gradient(200deg, #FFFFFF 0%, #EEEEEE 18%, #A0A0A0 73%, #979797 100%)"
              backgroundClip="text"
              mr="6"
            >
              CAR SELECTION
            </Heading>
          </Flex>
          {/* <Button
            variant="secondary"
            borderRadius="3.125rem"
            gap="0.5rem"
            px="0.5rem"
            h="1.75rem"
            onClick={() => setShowCarStats((oldState) => !oldState)}
          >
            <Text
              fontFamily="heading"
              color="white.80"
              fontSize="0.875rem"
              lineHeight="1.25rem"
              textTransform="capitalize"
              fontWeight={400}
            >
              Stats
            </Text>
            {!showCarStats ? (
              <IconPlus w="1rem" h="1rem" />
            ) : (
              <IconMinus w="1rem" h="1rem" />
            )}
          </Button> */}
        </Flex>

        <Flex flexDir="column" gap="0.75rem">
          {cars && (
            <CarSelect
              cars={cars}
              trackCodeName={race?.track?.codeName}
              withLeaderboardAvailable={withLeaderboardAvailable}
              selectedCarIndex={selectedCarIndex}
              playersParticipants={race?.playersParticipants}
              setSelectedCarIndex={(value) => {
                setSelectedCarIndex(value);
                setWithLeaderboardEntry(false);
              }}
              lockedCarIds={getLockedCarIds()}
              eligibleCars={eligibleCars}
              raceLevel={(race?.difficulty || 0) + 1}
              onUpgradeClick={handleUpgradeClick}
            />
          )}
          {showCarStats && selectedCar?.stats && (
            <Flex alignItems="center" justifyContent="center" w="full">
              <FusionCarStats
                stats={getCarStats()}
                isLocked={selectedCar?.tier?.rank === 0}
                carLevel={rarityLevels[selectedCar?.rarity as keyof typeof rarityLevels] || 0}
                isCarStats={true}
                px={{ base: '0.25rem', md: '0.5rem' }}
              />
            </Flex>
          )}
        </Flex>
          

        {raceTier !== Tier.Tournament && raceTier !== 'weeklytournament' && selectedCar?.tier?.rank !== 0 && race?.difficulty !== 0&& (
          <Flex w="full" px="1" mt="1.5rem">
            <TournamentEntryAccordion
              isSelected={withLeaderboardEntry}
              setIsSelected={setWithLeaderboardEntry}
              selectedCarAvailable={selectedCarAvailable}
            >
              <LeaderboardAccordionPanel />
            </TournamentEntryAccordion>
          </Flex>
        )}
      </Box>

      <TicketCheckoutModal 
        isOpen={isOpen}
        onClose={onClose}
        customQuantity={admissionFee}
      />
    </>
  );
};

export default SelectCar;